import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import CSS from "csstype";
import "./../ResultsDialog/ResultsDialog.css";
import { disconnect, setMaxListeners } from "process";
import Dialog from "../Dialog/Dialog";

import {
  TurnInfo,
  Submissions,
  Results,
  PlayerEndResults,
  PlayerResultInfo,
  Cue,
  ScoredSwap,
  CueTypes,
  Question,
} from "../../shared/interfaces/GameLogicTypes";
import {
  hasPlayerAnswered,
  getPlayerName,
  getPlayerResults,
  getPlayerSequence,
  getPlayerSwaps,
  getPlayerScore,
  applyScoredSwap,
  getPlayerSubmissionResultList,
  getCzarGroupSummary,
  GroupPredictionTier,
} from "./../ResultsDialog/ResultsDialogLogic";
import ResultsSequence from "../ResultsSequence/ResultsSequence";
import SequenceComparison from "../SequenceComparison/SequenceComparison";
import { LaserRingPlayer } from "../SequenceComparison/VersusLaserRing";
import _ from "underscore";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { Flipped, Flipper } from "react-flip-toolkit";
import {
  Backdrop,
  ClickAwayListener,
  Paper,
  Stack,
  Button,
  Divider,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { getColorStylebyNumber, getFontSizeByAnswer } from "../../shared/helper/functions";
import { renderCueContent } from "../../shared/helper/renderCueContent";
import QuestionHeader from "../AlternateQuestionHeader/AlternateQuestionHeader";
import VersusBreakdown, { BreakdownPlayer } from "../VersusBreakdown/VersusBreakdown";
import { getPlayerColor } from "../PlayerAvatar/PlayerAvatar";
import useSocketContext from "../../shared/useSocketContext";
import MobileResultsSequence from "../MobileResultsSequence/MobileResultsSequence";

export interface MobileResults {
  isTsar: boolean;
  isHost: boolean;
  tsarId?: string;
  question: Question;
  playerId: string;
  results: Results;
  cues: Cue[];
  waitForHost: boolean;
  readyPlayerIds: string[];
  isReady: boolean;
  onReadyToggle: (next: boolean) => void;
  totalPlayers: number;
  // STANDARD_VERSUS: fires the group laser on the comparison's hero pop.
  laserPlayers?: LaserRingPlayer[];
  /** STANDARD_VERSUS: nobody is being predicted — everyone answered for themselves and is scored
   *  against the group's aggregate. Swaps the "how well you know X" wording for alignment wording. */
  isVersus?: boolean;
  /** STANDARD_VERSUS: the persisted aggregate ranking. It is not represented as a player. */
  groupRanking?: number[];
}

export default function MobileResults(props: MobileResults) {
  const [tsarName, setTsarName] = useState("");
  const [tsarAnswered, setTsarAnswered] = useState(false);
  const [tsarSequence, setTsarSequence] = useState<number[]>([]);
  const [playerName, setPlayerName] = useState("");
  const [playerAnswered, setPlayerAnswered] = useState(false);
  const [playerSequence, setPlayerSequence] = useState<number[]>([]);
  const [playerSwaps, setPlayerSwaps] = useState<ScoredSwap[]>([]);
  const [playerScore, setPlayerScore] = useState(2000);
  const [currentSwapIndex, setCurrentSwapIndex] = useState(0);
  //wether the traversion over the swap array is forwards or backwards
  const [swapForward, setSwapForward] = useState(true);
  const [currentScore, setCurrentScore] = useState(2000);
  const [readyForAnimation, setReadyForAnimation] = useState(false);
  //"Myresults, Allresults
  const [showFinalScore, setShowFinalScore] = useState(false);
  const [currentView, setCurrentView] = useState(
    props.isTsar && props.totalPlayers > 2 ? "A" : "M"
  );
  const [changingSequence, setChangingSequence] = useState(false);
  const [showFinalScoreTimerId, setShowFinalScoreTimerId] = useState<NodeJS.Timeout | undefined>(
    undefined
  );
  const [imageSequence, setImageSequence] = useState([1, 2, 3, 4, 5]);
  // Staged reveal: 0=intro text, 1=tsar sequence, 2=transition text, 3=player sequence+scoring
  const [revealStage, setRevealStage] = useState(0);
  const [cardsPlopping, setCardsPlopping] = useState(false);
  const [sortedNonTsarResults, setSortedNonTsarResults] = useState<
    {
      playerId: string;
      playerName: string;

      score: number;
      sequence: number[];
    }[]
  >([]);
  let socket = useSocketContext().socket;

  const groupSummary = useMemo(
    () => getCzarGroupSummary(sortedNonTsarResults),
    [sortedNonTsarResults]
  );

  // Versus all-results: who put which option where (see VersusBreakdown).
  const breakdownPlayers: BreakdownPlayer[] = useMemo(
    () =>
      sortedNonTsarResults.map((r, i) => ({
        id: r.playerId,
        name: r.playerName,
        color: props.laserPlayers?.find((p) => p.id === r.playerId)?.color ?? getPlayerColor(i),
        sequence: r.sequence,
      })),
    [sortedNonTsarResults, props.laserPlayers]
  );

  const isDuo = sortedNonTsarResults.length === 1;
  const duoName = isDuo ? sortedNonTsarResults[0].playerName : "";
  // 2-player (single guesser): use the angled comparison view for both roles.
  const useComparison = props.totalPlayers <= 2;

  const progressionControls = props.waitForHost ? (
    props.isHost ? (
      <Button
        variant="contained"
        sx={{ height: { md: "3em", xl: "3em" } }}
        onClick={() => socket.emit("nextTurn")}
      >
        {t("start_next_round")}
      </Button>
    ) : (
      <Typography sx={{ userSelect: "none" }} variant="h6">
        {t("waiting_next_round")}
      </Typography>
    )
  ) : (
    <Button
      variant={props.isReady ? "outlined" : "contained"}
      sx={{ height: { md: "3em", xl: "3em" } }}
      onClick={() => props.onReadyToggle(!props.isReady)}
    >
      {props.isReady ? t("unready") : t("ready")} ({props.readyPlayerIds.length}/
      {props.totalPlayers})
    </Button>
  );

  const tierMessageKey: Record<GroupPredictionTier, string> = isDuo
    ? {
        perfect: "czar_tier_perfect_duo",
        great: "czar_tier_great_duo",
        good: "czar_tier_good_duo",
        average: "czar_tier_average_duo",
        bad: "czar_tier_bad_duo",
        terrible: "czar_tier_terrible_duo",
      }
    : {
        perfect: "czar_tier_perfect",
        great: "czar_tier_great",
        good: "czar_tier_good",
        average: "czar_tier_average",
        bad: "czar_tier_bad",
        terrible: "czar_tier_terrible",
      };

  // Non-czar's own-prediction tier sentences (how well YOU know the answerer)
  const playerTierMessageKey: Record<GroupPredictionTier, string> = {
    perfect: "player_tier_perfect",
    great: "player_tier_great",
    good: "player_tier_good",
    average: "player_tier_average",
    bad: "player_tier_bad",
    terrible: "player_tier_terrible",
  };

  // Versus: there is no one to predict, so the tiers talk about alignment with the group instead.
  const versusTierMessageKey: Record<GroupPredictionTier, string> = {
    perfect: "versus_tier_perfect",
    great: "versus_tier_great",
    good: "versus_tier_good",
    average: "versus_tier_average",
    bad: "versus_tier_bad",
    terrible: "versus_tier_terrible",
  };

  const tierColor: Record<GroupPredictionTier, string> = {
    perfect: "#ffd700",
    great: "#4caf50",
    good: "#66bb6a",
    average: "text.secondary",
    bad: "#ff9800",
    terrible: "#f44336",
  };

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        left: `${(i * 2.5 + Math.sin(i * 7) * 12 + 50) % 100}%`,
        delay: `${(i * 0.07) % 2.5}s`,
        duration: `${2.2 + (i % 5) * 0.4}s`,
        color: ["#ffd700", "#ff4081", "#00e5ff", "#76ff03", "#7c4dff", "#ff6d00"][i % 6],
        width: 7 + (i % 4) * 2,
        height: 5 + (i % 3) * 2,
        rotation: 360 + (i % 4) * 180,
      })),
    []
  );

  useEffect(() => {
    //constructor
    const referencePlayerId = props.tsarId ?? "";
    const hasVersusGroupRanking =
      props.isVersus &&
      props.groupRanking?.length === 5 &&
      new Set(props.groupRanking).size === 5;
    const referenceSequence = hasVersusGroupRanking
      ? [...props.groupRanking!]
      : getPlayerSequence(props.results, referencePlayerId);
    const referenceAnswered = hasVersusGroupRanking
      ? true
      : hasPlayerAnswered(props.results, referencePlayerId);

    setSwapForward(true);
    setTsarName(
      hasVersusGroupRanking
        ? t("versus_group_label")
        : getPlayerName(props.results, referencePlayerId)
    );
    setTsarAnswered(referenceAnswered);
    setTsarSequence(referenceSequence);
    setPlayerName(getPlayerName(props.results, props.playerId));
    setPlayerAnswered(hasPlayerAnswered(props.results, props.playerId));
    // Start at the comparison reference and animate toward the player's submission.
    setPlayerSequence(referenceAnswered ? referenceSequence : [1, 2, 3, 4, 5]);
    //inserting Pseudo Element to have an animation startingPoint
    let emptySwapSequence: Array<ScoredSwap> = [];
    const rawSwaps = hasPlayerAnswered(props.results, props.playerId)
      ? getPlayerSwaps(props.results, props.playerId)
      : emptySwapSequence;
    setPlayerSwaps(_.union([{ score: 0, swap: [0, 0] }], [...rawSwaps].reverse()));
    setImageSequence(referenceAnswered ? referenceSequence : [1, 2, 3, 4, 5]);
    setPlayerScore(2000);
    // For non-tsar players, delay animation start until reveal stage 3
    if (props.isTsar) setReadyForAnimation(true);
    setSortedNonTsarResults(
      getPlayerSubmissionResultList(
        props.results,
        hasVersusGroupRanking ? "" : referencePlayerId
      )
    );

    return () => {
      setSwapForward(true);
      setCurrentSwapIndex(0);
      setCurrentView(props.isTsar ? "A" : "M");
      setRevealStage(0);
    };
    //deconstructor
  }, []);

  // Staged reveal timer
  useEffect(() => {
    if (props.isTsar || currentView !== "M" || !playerAnswered) return;
    setRevealStage(0);
    const timers: NodeJS.Timeout[] = [];
    timers.push(setTimeout(() => setRevealStage(1), 800));
    timers.push(setTimeout(() => setRevealStage(2), 2200));
    timers.push(setTimeout(() => setRevealStage(3), 3200));
    return () => timers.forEach(clearTimeout);
  }, [currentView, playerAnswered, props.isTsar]);

  // Trigger card plop animation one frame after cards mount
  useEffect(() => {
    if (revealStage >= 1 && !cardsPlopping) {
      requestAnimationFrame(() => setCardsPlopping(true));
    }
    if (revealStage === 0) setCardsPlopping(false);
  }, [revealStage]);

  // Start swap animation when reveal reaches stage 3
  useEffect(() => {
    if (revealStage === 3 && !props.isTsar) {
      setReadyForAnimation(true);
    }
  }, [revealStage, props.isTsar]);

  useEffect(
    () => {
      let animationTimer: NodeJS.Timeout;
      if (playerSwaps.length != 0 && readyForAnimation) {
        animationTimer = setTimeout(() => {
          if (playerSwaps.length > currentSwapIndex + 1) {
            setCurrentSwapIndex(currentSwapIndex + 1);
          }
        }, 1100);
      }
      return () => {
        clearTimeout(animationTimer);
      };
    },
    // useEffect will run only one time with empty []
    // if you pass a value to array,
    // like this - [data]
    // than clearTimeout will run every time
    // this value changes (useEffect re-run)
    [readyForAnimation, playerSequence]
  );

  useEffect(() => {
    if (playerSequence.length != 0) {
      console.log("playerSwaps: " + JSON.stringify(playerSwaps));
      if (swapForward) {
        console.log("trying to swap forward with currentswapIndex: " + currentSwapIndex);
        const currentSwap = playerSwaps[currentSwapIndex];
        console.log("currentswap: " + JSON.stringify(currentSwap));
        const newSequence = applyScoredSwap(playerSequence.slice(), currentSwap);
        setPlayerSequence(newSequence);
        setPlayerScore(playerScore - currentSwap.score);
      } else {
        console.log("trying to swap backwards with currentswapIndex: " + currentSwapIndex);
        const nextSwap = playerSwaps[currentSwapIndex + 1];
        console.log("nextSwap: " + JSON.stringify(nextSwap));
        const newSequence = applyScoredSwap(playerSequence.slice(), nextSwap);
        setPlayerSequence(newSequence);
        setPlayerScore(playerScore + nextSwap.score);
      }
      console.log("completed sequenceChange sucessfully");
    }
  }, [currentSwapIndex]);

  //playerScore taking damage animation
  useEffect(() => {
    let animationTimer: NodeJS.Timeout;

    setChangingSequence(true);
    animationTimer = setTimeout(() => {
      setChangingSequence(false);
    }, 30);

    // this will clear Timeout
    // when component unmount like in willComponentUnmount
    // and show will not change to true
    return () => {
      clearTimeout(animationTimer);
    };
  }, [playerScore]);

  useEffect(() => {
    console.log("sequence changed " + playerSequence);
  }, [playerSequence]);

  const handleGotoAllResults = () => {
    setCurrentView("A");
    setSwapForward(true);
    setCurrentSwapIndex(0);
    setPlayerSequence(tsarSequence.length > 0 ? [...tsarSequence] : [1, 2, 3, 4, 5]);
    setReadyForAnimation(false);
    setPlayerScore(2000);
    console.log("reset everything");
  };

  useEffect(() => {
    if (!(playerSwaps.length > currentSwapIndex + 1)) {
      setShowFinalScoreTimerId(
        setTimeout(() => {
          setShowFinalScore(true);
        }, 1200)
      );
    } else {
      setShowFinalScore(false);
      if (showFinalScoreTimerId) {
        clearTimeout(showFinalScoreTimerId);
      }
    }
  }, [currentSwapIndex]);

  // The comparison view is compact, so let the panel shrink to its content
  // (avoids a tall panel with lots of empty space). The scrollable "all
  // results" list still needs the full height.
  const showComparison =
    currentView === "M" &&
    ((props.isTsar && useComparison && tsarAnswered && groupSummary.answeredCount > 0) ||
      (!props.isTsar && playerAnswered));

  return (
    <div>
      <Stack justifyContent="center" alignItems={"center"}>
        <Paper
          sx={{
            height: showComparison ? "auto" : "78vh",
            maxHeight: "85vh",
            width: "90vw",
            padding: "1em",
          }}
        >
          {currentView == "M" ? (
            //MYRESULTS
            <Stack
              justifyContent={"space-between"}
              alignItems="center"
              sx={{ width: "100%", height: "100%" }}
            >
                {/* props.isTsar */}
              {props.isTsar ? (
                //TSARVIEW
                tsarAnswered == true ? (
                  useComparison && groupSummary.answeredCount > 0 ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", flex: 1, minHeight: 0 }}>
                      <SequenceComparison
                        leftLabel={t("you_label")}
                        rightLabel={t("prediction_of_name", {
                          name: sortedNonTsarResults[0].playerName,
                        })}
                        leftSequence={tsarSequence}
                        rightSequence={sortedNonTsarResults[0].sequence}
                        score={sortedNonTsarResults[0].score}
                        cues={props.cues}
                        caption={t(tierMessageKey[groupSummary.tier], {
                          name: sortedNonTsarResults[0].playerName,
                        })}
                        mobile
                      />
                    </Stack>
                  ) : (
                  <Stack alignItems={"center"} justifyContent="space-around" sx={{height:"40%", position: "relative"}} >
                    {groupSummary.tier === "perfect" && (
                      <div className="czar-confetti-container">
                        {confettiPieces.map((p, i) => (
                          <div
                            key={i}
                            className="czar-confetti-piece"
                            style={{
                              left: p.left,
                              animationDelay: p.delay,
                              animationDuration: p.duration,
                              backgroundColor: p.color,
                              width: p.width,
                              height: p.height,
                              ["--confetti-rot" as any]: `${p.rotation}deg`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                    <Typography variant="h6" sx={{ fontSize: 22, userSelect:"none" }}>{t("your_answer")}</Typography>
                    <MobileResultsSequence
                      inputSequence={tsarSequence}
                      cues={props.cues}
                    />
                    {groupSummary.answeredCount > 0 ? (
                      <Stack alignItems="center" spacing={0.5}>
                        <Typography
                          className="czar-summary-message"
                          variant="h6"
                          textAlign="center"
                          sx={{ fontSize: 20, userSelect: "none" }}
                        >
                          {t(tierMessageKey[groupSummary.tier], { name: duoName })}
                        </Typography>
                        {(groupSummary.tier === "average" || groupSummary.tier === "bad" || groupSummary.tier === "terrible") && (
                          <Typography variant="body1" textAlign="center" sx={{ userSelect: "none" }}>
                            {t("explain_decision")}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="h6" sx={{ fontSize: 20, userSelect: "none" }}>
                        {t("czar_nobody_answered")}
                      </Typography>
                    )}
                  </Stack>
                  )
                ) : (
                  <Stack
                    alignItems={"center"}
                    sx={{ paddingTop: "1em", width: "100%", minHeight: "60%" }}
                  >
                    <Typography variant="h3">{t("did_not_answer")}</Typography>
                  </Stack>
                )
                
              ) : playerAnswered == true ? (
                //PLAYERVIEW — angled comparison
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: "100%", flex: 1, minHeight: 0 }}
                >
                  <SequenceComparison
                    leftLabel={tsarName}
                    rightLabel={t(props.isVersus ? "versus_your_ranking" : "your_prediction")}
                    leftSequence={tsarSequence}
                    rightSequence={getPlayerSequence(props.results, props.playerId)}
                    score={getPlayerScore(props.results, props.playerId)}
                    cues={props.cues}
                    caption={t(
                      (props.isVersus ? versusTierMessageKey : playerTierMessageKey)[
                        getCzarGroupSummary([
                          { score: getPlayerScore(props.results, props.playerId) },
                        ]).tier
                      ],
                      { name: tsarName }
                    )}
                    mobile
                    laserPlayers={props.laserPlayers}
                  />
                </Stack>
              ) : (
                <Typography textAlign={"center"} sx={{ fontSize: 30 , userSelect:"none"}} variant="h1">
                  {t("you_did_not_answer")}
                </Typography>
              )}

              {useComparison ? (
                <Box sx={{ alignSelf: "flex-end" }}>{progressionControls}</Box>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    height: "3em",
                    alignSelf: "flex-end",
                  }}
                  onClick={handleGotoAllResults}
                >
                  {t("see_all")}
                </Button>
              )}
            </Stack>
          ) : (
            //ALL RESULTS

            <>
              <Stack justifyContent={"space-between"} sx={{ height: "100%", width: "100%" }}>
                <Stack
                  justifyContent={"flex-start"}
                  sx={{ height: "calc(100% - 3em)", width: "100%", minHeight: 0 }}
                >
                  {props.isTsar && groupSummary.answeredCount > 0 && (
                    <Stack alignItems="center" sx={{ position: "relative" }}>
                      {groupSummary.tier === "perfect" && (
                        <div className="czar-confetti-container">
                          {confettiPieces.map((p, i) => (
                            <div
                              key={i}
                              className="czar-confetti-piece"
                              style={{
                                left: p.left,
                                animationDelay: p.delay,
                                animationDuration: p.duration,
                                backgroundColor: p.color,
                                width: p.width,
                                height: p.height,
                                ["--confetti-rot" as any]: `${p.rotation}deg`,
                              }}
                            />
                          ))}
                        </div>
                      )}
                      <Typography
                        className="czar-summary-message"
                        variant="h6"
                        textAlign="center"
                        sx={{ fontSize: 18, userSelect: "none", paddingBottom: ".3em" }}
                      >
                        {t(tierMessageKey[groupSummary.tier], { name: duoName })}
                      </Typography>
                    </Stack>
                  )}
                  {props.isVersus ? (
                    // Versus: per-option breakdown instead of a stack of five-card sequences —
                    // who agreed with the group on a given option is the interesting part.
                    <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", width: "100%", paddingBlock: "1vh" }}>
                      <VersusBreakdown
                        mobile
                        groupRanking={tsarSequence}
                        cues={props.cues}
                        players={breakdownPlayers}
                        viewerId={props.playerId}
                      />
                    </Stack>
                  ) : (
                  <>
                  <Stack justifyContent={"space-around"}>
                    <MobileResultsSequence
                      inputSequence={tsarSequence}
                      playerName={tsarName}
                      cues={props.cues}
                    />

                    <Divider orientation="horizontal" sx={{ width: "90%" }} />
                  </Stack>
                  <Stack
                    justifyContent={"flex-start"}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      width: "100%",
                      paddingBlock: "2vh",
                      borderRadius: "var(--border-radius)",
                    }}
                    spacing={2}

                  >
                    {sortedNonTsarResults
                      .map((results) => {
                        return (
                          <MobileResultsSequence
                            inputSequence={results.sequence}
                            playerName={results.playerName}
                            score={results.score}
                            cues={props.cues}
                          />
                        );
                      })}
                  </Stack>
                  </>
                  )}
                </Stack>
                <Stack
                  direction={"row"}
                  justifyContent="space-between"
                  alignItems={"flex-end"}
                  sx={{ width: "100%", height: "3em" }}
                >
                  {!props.isTsar ? (
                    <Button
                      variant="contained"
                      sx={{
                        height: { md: "3em", xl: "3em" },
                      }}
                      onClick={() => {
                        setRevealStage(0);
                        setCardsPlopping(false);
                        setReadyForAnimation(false);
                        setSwapForward(true);
                        setCurrentSwapIndex(0);
                        setPlayerSequence(tsarSequence.length > 0 ? [...tsarSequence] : [1, 2, 3, 4, 5]);
                        setPlayerScore(2000);
                        setCurrentView("M");
                      }}
                    >
                      {t("see_mine")}
                    </Button>
                  ) : <Box />}
                  {progressionControls}
                </Stack>
              </Stack>
            </>
          )}
        </Paper>
      </Stack>
    </div>
  );

  // if (interpretedResults != undefined && interpretedResults["iAnswered"]) {
  // }
}
